import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Clock, Eye, Tag, Share, LinkedinLogo, TwitterLogo,
  ArrowRight, BookOpen, User
} from "@phosphor-icons/react";
import axios from "axios";
import NewsletterPopup from "../components/NewsletterPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(res.data.post);
      setRelated(res.data.related || []);
    } catch (error) {
      console.error("Error fetching post:", error);
      if (error.response?.status === 404) {
        navigate('/blog');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="max-w-4xl mx-auto container-padding">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-slate-200 rounded w-3/4" />
            <div className="h-64 bg-slate-200 rounded-2xl" />
            <div className="space-y-4">
              <div className="h-4 bg-slate-200 rounded" />
              <div className="h-4 bg-slate-200 rounded w-5/6" />
              <div className="h-4 bg-slate-200 rounded w-4/5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <BookOpen weight="duotone" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-slate-900 mb-4">Article not found</h1>
          <Link to="/blog" className="text-cyan-600 hover:text-cyan-700">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-article-page" className="bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient opacity-50" />
        
        <div className="max-w-4xl mx-auto container-padding relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-cyan-600 transition-colors mb-8"
              data-testid="back-to-blog"
            >
              <ArrowLeft weight="bold" className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-medium text-cyan-600 bg-cyan-50 px-4 py-1.5 rounded-full">
                {post.category}
              </span>
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Clock weight="fill" className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <Eye weight="fill" className="w-4 h-4" />
                {post.views} views
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl text-slate-900 mb-6 leading-tight tracking-tight">
              {post.title}
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                  <User weight="fill" className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{post.author}</p>
                  <p className="text-sm text-slate-500">Noble Healthcare Team</p>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">Share:</span>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-cyan-500 hover:text-white transition-all"
                  data-testid="share-linkedin"
                >
                  <LinkedinLogo weight="fill" className="w-5 h-5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-cyan-500 hover:text-white transition-all"
                  data-testid="share-twitter"
                >
                  <TwitterLogo weight="fill" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="py-8">
          <div className="max-w-5xl mx-auto container-padding">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full aspect-video object-cover"
                data-testid="article-image"
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto container-padding">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="prose prose-lg prose-slate max-w-none"
            data-testid="article-content"
          >
            <style>{`
              .prose h2 {
                font-family: 'Outfit', sans-serif;
                color: #0f172a;
                margin-top: 2em;
                margin-bottom: 1em;
              }
              .prose h3 {
                font-family: 'Outfit', sans-serif;
                color: #0f172a;
              }
              .prose p {
                color: #475569;
                line-height: 1.8;
              }
              .prose a {
                color: #06b6d4;
                text-decoration: none;
              }
              .prose a:hover {
                text-decoration: underline;
              }
              .prose ul, .prose ol {
                color: #475569;
              }
              .prose li {
                margin-top: 0.5em;
                margin-bottom: 0.5em;
              }
              .prose blockquote {
                border-left-color: #06b6d4;
                background: #f8fafc;
                padding: 1em;
                border-radius: 0.5rem;
              }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </motion.article>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-3 flex-wrap">
                <Tag weight="fill" className="w-5 h-5 text-slate-400" />
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${tag}`}
                    className="text-sm bg-slate-100 text-slate-600 px-4 py-1.5 rounded-full hover:bg-cyan-50 hover:text-cyan-600 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="font-display text-3xl text-white mb-4">
            Ready to Transform Your Hospice Operations?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Schedule a free consultation with our hospice compliance and operations experts.
          </p>
          <Link
            to="/contact"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2 hover:shadow-xl hover:shadow-cyan-500/30 transition-all"
            data-testid="article-cta"
          >
            Book Free Consultation
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto container-padding">
            <h2 className="font-display text-2xl text-slate-900 mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((relatedPost) => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-xl hover:border-cyan-500/30 transition-all duration-300 group"
                  data-testid={`related-${relatedPost.slug}`}
                >
                  {relatedPost.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.featured_image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-display font-bold text-lg text-slate-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Popup - triggers after 50% scroll */}
      <NewsletterPopup trigger="scroll" />
    </div>
  );
};

export default BlogArticlePage;
